import {type ReactElement, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useLoaderData, useNavigate} from 'react-router';

import {Document} from '@/components/document/document';
import {DocumentMigrationHelper} from '@/components/document/document-migration-helper';
import {
  isDocumentModelVersionTooHigh,
  isDocumentMigrationNeeded as isDocumentMigrationNeeded,
} from '@/components/document/model-version/compare-model-versions';
import {createDocumentStore} from '@/components/document/store';
import {ConfigContext, useConfig} from '@/contexts/config';
import {createValueResolver} from '@/engine';
import {useModelQuery} from '@/hooks/queries/use-model-query';
import {Toaster} from '@/initializers/toaster';
import {Configuration} from '@/model/types';
import {getById} from '@/repositories/beneva-api/endpoints/documents';
import {type FormLoader} from './loader';

type FormLoaderResponse = Exclude<Awaited<ReturnType<typeof FormLoader>>, Response>;

type Props = Omit<FormLoaderResponse, 'documentConfig' | 'debugOverrideVersion' | 'debugOverrideConfig'>;

const Form = ({document, allocations, attachments}: Props) => {
  const {t} = useTranslation();
  const {model} = useConfig();
  const navigate = useNavigate();

  const isVersionTooHigh = isDocumentModelVersionTooHigh(document.modelVersion, model.version);

  // Early return if the document's model version is higher than the app; this can only happen in debug mode
  useEffect(() => {
    if (isVersionTooHigh) {
      Toaster.error(t('components.document.migration.version-too-high'));
      navigate('/', {replace: true});
    }
  }, [isVersionTooHigh, navigate, t]);

  if (isVersionTooHigh) {
    return null;
  }

  if (isDocumentMigrationNeeded(document.modelVersion, model.version)) {
    return <DocumentMigrationHelper document={document} targetVersion={model.version} />;
  }

  return <Document key={document.id} document={document} allocations={allocations} attachments={attachments} />;
};

export default function FormConfig(): ReactElement | null {
  const {
    documentConfig,
    debugOverrideVersion,
    debugOverrideConfig,
    document: loaderDocument,
    ...props
  } = useLoaderData<FormLoaderResponse>();

  const {data: document} = useQuery({
    queryKey: ['document', loaderDocument.id],
    queryFn: async () => {
      const {data} = await getById(loaderDocument.id);
      return data;
    },
    initialData: loaderDocument,
  });

  const configOverride = resolveConfigOverride(
    debugOverrideVersion,
    debugOverrideConfig,
    documentConfig,
    document.status,
  );

  const {data: modelOverride, isLoading} = useModelQuery(configOverride ?? documentConfig, {enabled: !!configOverride});

  if (!configOverride) {
    return <Form document={document} {...props} />;
  }

  if (isLoading || !modelOverride) {
    return null;
  }

  const domainValueResolver = createValueResolver(createDocumentStore(), modelOverride.values || []);

  return (
    <ConfigContext.Provider value={{model: modelOverride, domainValueResolver}}>
      <Form document={document} {...props} />
    </ConfigContext.Provider>
  );
}

function resolveConfigOverride(
  debugOverrideVersion: string | null,
  debugOverrideConfig: Configuration | undefined,
  documentConfig: Configuration,
  documentStatus: string,
): Configuration | null {
  // Completed documents always render with the config they were submitted under; the debug override never applies.
  const shouldUseDocumentConfig = documentStatus !== 'incomplete';

  if (shouldUseDocumentConfig) return documentConfig;

  if (!debugOverrideVersion) return null;

  if (debugOverrideVersion === documentConfig.version) return documentConfig;

  return debugOverrideConfig ?? null;
}
